// ============================================================
//  GameScreen.jsx  —  Main gameplay component
//
//  Key mechanics:
//  • Each page has one AI-generated (or Wikidata-fallback) anomaly
//  • Clicking the anomaly link NAVIGATES to that entity's Wikipedia
//    page, which becomes the next game page (theme-consistent chain)
//  • Wrong clicks trigger a 5-second cooldown (all difficulties)
//  • Hints:  Scholar → unlimited per page
//            Detective → 2 total for the entire game
//            Oracle → none (unless Fun Mode is on)
//  • Fun Mode → unlimited hints + zero cooldown on wrong clicks
//  • Win when all pages are completed
// ============================================================

import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { buildGamePage, fetchThemeTitle, resetSession, THEMES } from "./wikiAPI";

// ── Constants ────────────────────────────────────────────────

// Hint limits per difficulty (game-total, not per-page)
// Scholar:  Infinity — unlimited, checks per-page availability only
// Detective: 2       — 2 hints for the WHOLE game (precious resource)
// Oracle:    0       — no hints at all (overridden by Fun Mode)
const HINT_LIMITS    = { scholar: Infinity, detective: 2, oracle: 0 };
const TOTAL_PAGES    = { scholar: 4, detective: 6, oracle: 9 };
// Wrong-click cooldown scales with difficulty
const WRONG_COOLDOWNS = { scholar: 5, detective: 10, oracle: 15 };

// ── Global styles ────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@700;900&family=Crimson+Pro:wght@400;600&family=Space+Mono:wght@400;700&display=swap');
  :root{
    --bg:#0a0a0f; --surface:#12121a; --surface2:#1a1a26;
    --border:rgba(255,255,255,0.08); --gold:#d4a94a; --gold-lt:#f0cc7a;
    --gold-dim:rgba(212,169,74,0.15); --text:#e8e4d8; --muted:#6b6880;
    --green:#4ade80; --red:#f87171; --blue:#60a5fa; --purple:#a78bfa;
    --radius:6px;
  }

  /* ── TOP BAR ─────────────────────────────────────────────── */
  .game-topbar{
    position:sticky; top:0; z-index:50;
    background:var(--surface); border-bottom:1px solid var(--border);
    display:flex; align-items:center; justify-content:space-between;
    padding:0.55rem 1.25rem; gap:0.75rem; flex-wrap:wrap;
  }
  .topbar-brand{
    display:flex; align-items:center; gap:0.5rem; white-space:nowrap;
  }
  .topbar-logo{
    font-size:1.1rem; line-height:1;
    filter:drop-shadow(0 0 6px rgba(212,169,74,0.5));
  }
  .topbar-title{
    font-family:'Cinzel',serif; font-size:0.85rem;
    font-weight:700; color:var(--gold);
  }
  .topbar-meta{
    display:flex; align-items:center; gap:0.35rem; margin-left:0.3rem;
  }
  .topbar-chip{
    font-family:'Space Mono',monospace; font-size:0.48rem;
    letter-spacing:0.07em; text-transform:uppercase;
    padding:2px 6px; border-radius:3px; line-height:1.5;
    border:1px solid; opacity:0.82;
  }
  .chip-theme{
    background:rgba(212,169,74,0.1); color:var(--gold);
    border-color:rgba(212,169,74,0.3);
  }
  .chip-scholar{ background:rgba(74,222,128,0.08); color:#4ade80; border-color:rgba(74,222,128,0.3); }
  .chip-detective{ background:rgba(250,204,21,0.08); color:#facc15; border-color:rgba(250,204,21,0.3); }
  .chip-oracle{ background:rgba(248,113,113,0.08); color:#f87171; border-color:rgba(248,113,113,0.3); }
  .topbar-progress{ flex:1; text-align:center; min-width:140px; }
  .prog-label{
    font-family:'Space Mono',monospace; font-size:0.5rem;
    letter-spacing:0.18em; text-transform:uppercase;
    color:var(--muted); display:block; margin-bottom:0.22rem;
  }
  .prog-dots{ display:flex; gap:6px; justify-content:center; align-items:center; }
  .prog-dot{
    width:10px; height:10px; border-radius:50%;
    transition:background 0.25s, border-color 0.25s, box-shadow 0.25s;
  }
  .prog-dot.done  { background:var(--green); border:1px solid var(--green); box-shadow:0 0 8px rgba(74,222,128,0.4); }
  .prog-dot.active{ background:var(--gold);  border:1px solid var(--gold); }
  .prog-dot.todo  { background:transparent;  border:1px solid rgba(255,255,255,0.15); }
  .prog-text{
    font-family:'Space Mono',monospace; font-size:0.65rem;
    color:var(--gold-lt); margin-top:0.18rem; display:block;
  }
  .topbar-stats{ display:flex; gap:1rem; align-items:center; }
  .tstat{ text-align:center; }
  .tstat .ts-label{
    font-family:'Space Mono',monospace; font-size:0.5rem;
    letter-spacing:0.12em; text-transform:uppercase; color:var(--muted); display:block;
  }
  .tstat .ts-value{ font-family:'Space Mono',monospace; font-size:0.88rem; color:var(--text); }
  .fun-mode-badge{
    font-family:'Space Mono',monospace; font-size:0.5rem;
    letter-spacing:0.1em; padding:0.18rem 0.45rem;
    background:rgba(212,169,74,0.15); color:var(--gold);
    border:1px solid rgba(212,169,74,0.35); border-radius:3px;
    white-space:nowrap;
  }
  .btn-quit{
    background:none; border:1px solid var(--border); color:var(--muted);
    border-radius:var(--radius); padding:0.26rem 0.6rem;
    font-family:'Space Mono',monospace; font-size:0.58rem;
    cursor:pointer; letter-spacing:0.1em; transition:all 0.15s;
  }
  .btn-quit:hover{ border-color:var(--red); color:var(--red); }


  /* ── CLUE REVEAL PANEL ───────────────────────────────────── */
  .clue-panel{
    position:fixed; top:50%; left:50%;
    transform:translate(-50%,-50%) scale(0.92);
    z-index:500; opacity:0;
    transition:opacity 0.3s, transform 0.3s;
    pointer-events:none;
    background:linear-gradient(135deg, #0f0a1a, #1a0f2e);
    border:1px solid var(--purple);
    border-left:4px solid var(--purple);
    border-radius:8px; padding:1.5rem 2rem;
    min-width:320px; max-width:520px; text-align:center;
    box-shadow:0 0 60px rgba(167,139,250,0.3);
  }
  .clue-panel.show{
    opacity:1; transform:translate(-50%,-50%) scale(1); pointer-events:auto;
  }
  .clue-eyebrow{
    font-family:'Space Mono',monospace; font-size:0.55rem;
    letter-spacing:0.25em; text-transform:uppercase;
    color:var(--purple); margin-bottom:0.5rem; display:block;
  }
  .clue-number{
    font-family:'Cinzel',serif; font-size:0.85rem;
    color:rgba(167,139,250,0.7); margin-bottom:0.4rem; display:block;
  }
  .clue-text{
    font-family:'Crimson Pro',Georgia,serif; font-size:1.15rem;
    line-height:1.55; color:var(--text); margin-bottom:1rem; font-style:italic;
  }
  .clue-anomaly{
    font-family:'Space Mono',monospace; font-size:0.6rem;
    color:rgba(74,222,128,0.7); margin-bottom:1rem;
    padding:0.4rem 0.8rem; border:1px solid rgba(74,222,128,0.2);
    border-radius:4px; display:inline-block;
  }
  .btn-clue-continue{
    font-family:'Cinzel',serif; font-size:0.82rem; font-weight:700;
    letter-spacing:0.1em; text-transform:uppercase;
    background:var(--purple); color:#0a0a0f;
    border:none; border-radius:var(--radius);
    padding:0.7rem 2rem; cursor:pointer; transition:all 0.15s;
  }
  .btn-clue-continue:hover{ filter:brightness(1.15); }

  /* ── WRONG BANNER ────────────────────────────────────────── */
  .wrong-banner{
    background:#1a0808;
    border-top:2px solid rgba(248,113,113,0.5);
    border-bottom:2px solid rgba(248,113,113,0.5);
    padding:0.8rem 1.25rem;
    display:flex; align-items:center; justify-content:space-between;
    flex-wrap:wrap; gap:0.75rem;
  }
  .article-wrap.on-cooldown{ opacity:0.45; pointer-events:none; transition:opacity 0.2s; }
  .wrong-banner-left{ display:flex; flex-direction:column; gap:0.2rem; }
  .wrong-banner-title{ font-family:'Cinzel',serif; font-size:0.9rem; font-weight:700; color:var(--red); }
  .wrong-banner-msg{
    font-family:'Space Mono',monospace; font-size:0.65rem;
    color:rgba(248,113,113,0.65); line-height:1.45;
  }
  .btn-dismiss{
    font-family:'Space Mono',monospace; font-size:0.65rem;
    font-weight:700; letter-spacing:0.08em; text-transform:uppercase;
    background:transparent; color:var(--red);
    border:1px solid rgba(248,113,113,0.4); border-radius:var(--radius);
    padding:0.45rem 1rem; cursor:pointer; transition:all 0.15s;
  }
  .btn-dismiss:hover{ background:rgba(248,113,113,0.1); }

  /* ── RETRY BANNER ────────────────────────────────────────── */
  .retry-banner{
    background:#1a1400; border-bottom:2px solid var(--gold);
    padding:0.6rem 1.25rem;
    font-family:'Space Mono',monospace; font-size:0.7rem;
    color:var(--gold); text-align:center;
  }

  /* ── HINT BAR ────────────────────────────────────────────── */
  .hint-bar{
    background:var(--surface); border-bottom:1px solid var(--border);
    padding:0.48rem 1.25rem;
    display:flex; align-items:center; justify-content:space-between;
    flex-wrap:wrap; gap:0.5rem;
  }
  .hint-msg  { font-family:'Space Mono',monospace; font-size:0.68rem; color:var(--gold-lt); }
  .hint-count{ font-family:'Space Mono',monospace; font-size:0.6rem;  color:var(--muted);   }
  .btn-hint{
    font-family:'Space Mono',monospace; font-size:0.63rem; font-weight:700;
    letter-spacing:0.1em; text-transform:uppercase;
    background:var(--gold-dim); color:var(--gold);
    border:1px solid var(--gold); border-radius:var(--radius);
    padding:0.32rem 0.8rem; cursor:pointer; transition:all 0.15s;
  }
  .btn-hint:hover  { background:rgba(212,169,74,0.25); }
  .btn-hint:disabled{ opacity:0.3; cursor:not-allowed; }

  /* ── BREADCRUMB ──────────────────────────────────────────── */
  .breadcrumb-bar{
    background:var(--bg); border-bottom:1px solid var(--border);
    padding:0.4rem 1.25rem;
    display:flex; align-items:center; gap:0.3rem; flex-wrap:wrap;
    font-family:'Space Mono',monospace; font-size:0.6rem; overflow-x:auto;
  }
  .bc-crumb{ white-space:nowrap; padding:0.15rem 0.4rem; border-radius:3px; }
  .bc-crumb.correct{ color:var(--green); }
  .bc-crumb.current{ color:var(--gold-lt); font-weight:700; }
  .bc-sep{ color:var(--border); }

  /* ── FLASH ───────────────────────────────────────────────── */
  .flash-overlay{
    position:fixed; inset:0; z-index:200; pointer-events:none;
    opacity:0; transition:opacity 0.1s;
  }
  .flash-overlay.flash-red  { background:rgba(248,113,113,0.18); opacity:1; }
  .flash-overlay.flash-green{ background:rgba(74,222,128,0.18);  opacity:1; }

  /* ── TOAST ───────────────────────────────────────────────── */
  .toast{
    position:fixed; top:72px; left:50%;
    transform:translateX(-50%) translateY(-8px);
    z-index:300; padding:0.58rem 1.1rem; border-radius:var(--radius);
    font-family:'Space Mono',monospace; font-size:0.73rem; font-weight:700;
    letter-spacing:0.06em; opacity:0;
    transition:opacity 0.2s, transform 0.2s; pointer-events:none; white-space:nowrap;
  }
  .toast.show{ opacity:1; transform:translateX(-50%) translateY(0); }
  .toast.toast-red   { background:#3a1212; color:var(--red);    border:1px solid var(--red);    }
  .toast.toast-green { background:#0f2d1a; color:var(--green);  border:1px solid var(--green);  }
  .toast.toast-gold  { background:#2a1f00; color:var(--gold);   border:1px solid var(--gold);   }
  .toast.toast-purple{ background:#1a1030; color:var(--purple); border:1px solid var(--purple); }

  /* ── ARTICLE ─────────────────────────────────────────────── */
  /*
   * max-height keeps the page itself from scrolling.
   * overflow-y:auto lets the article area scroll internally if content
   * runs slightly long — the rest of the UI stays fixed in place.
   */
  .article-wrap{
    max-width:780px; margin:0 auto; padding:1.6rem 1.5rem 1.2rem; width:100%;
    max-height:calc(100vh - 155px); overflow-y:auto;
  }
  .article-wrap::-webkit-scrollbar{ width:4px; }
  .article-wrap::-webkit-scrollbar-track{ background:transparent; }
  .article-wrap::-webkit-scrollbar-thumb{ background:var(--border); border-radius:4px; }
  .article-title{
    font-family:'Cinzel',serif; font-size:1.65rem; font-weight:700;
    color:var(--gold-lt); border-bottom:1px solid var(--border);
    padding-bottom:0.5rem; margin-bottom:1rem;
  }
  .wiki-body{
    font-family:'Crimson Pro',Georgia,serif; font-size:1rem;
    line-height:1.78; color:var(--text);
  }
  .wiki-body p{ margin-bottom:0.88rem; }
  .wiki-body a,.wiki-link{
    color:var(--blue); text-decoration:underline;
    text-decoration-color:rgba(96,165,250,0.3);
    text-underline-offset:3px; cursor:pointer; transition:color 0.12s;
  }
  .wiki-body a:hover{ color:#93c5fd; }
  .anomaly-link{
    color:var(--blue); text-decoration:underline;
    text-decoration-color:rgba(96,165,250,0.3);
    text-underline-offset:3px; cursor:pointer;
  }

  /* ── ANOMALY REVEAL ──────────────────────────────────────── */
  .reveal-panel{
    position:fixed; bottom:2rem; left:50%;
    transform:translateX(-50%) translateY(20px);
    z-index:400; opacity:0; transition:opacity 0.3s, transform 0.3s;
    pointer-events:none;
    background:#0f2d1a; border:1px solid var(--green);
    border-left:4px solid var(--green); border-radius:var(--radius);
    padding:0.75rem 1.25rem; min-width:280px; max-width:480px; text-align:center;
  }
  .reveal-panel.show{ opacity:1; transform:translateX(-50%) translateY(0); }
  .reveal-panel .rp-title{
    font-family:'Space Mono',monospace; font-size:0.58rem;
    letter-spacing:0.2em; text-transform:uppercase;
    color:var(--green); margin-bottom:0.4rem; display:block;
  }
  .reveal-panel .rp-wrong{
    font-family:'Cinzel',serif; font-size:1rem;
    color:var(--red); text-decoration:line-through; opacity:0.8;
  }
  .reveal-panel .rp-arrow{ font-family:'Space Mono',monospace; font-size:0.8rem; color:var(--muted); margin:0 0.5rem; }
  .reveal-panel .rp-real { font-family:'Cinzel',serif; font-size:1rem; color:var(--green); }
  .reveal-panel .rp-label{
    font-family:'Space Mono',monospace; font-size:0.6rem;
    color:var(--muted); margin-top:0.25rem; display:block;
  }
  .reveal-panel .rp-source{
    font-family:'Space Mono',monospace; font-size:0.5rem;
    color:var(--purple); margin-top:0.2rem; display:block; letter-spacing:0.1em;
  }

  /* ── SKELETON ────────────────────────────────────────────── */
  .skeleton-wrap{ max-width:780px; margin:2rem auto; padding:0 1.5rem; display:flex; flex-direction:column; gap:0.58rem; }
  .skel{
    height:13px; border-radius:3px;
    background:linear-gradient(90deg,var(--surface) 25%,var(--surface2) 50%,var(--surface) 75%);
    background-size:400% 100%; animation:shimmer 1.3s infinite;
  }
  @keyframes shimmer{ 0%{background-position:100% 0} 100%{background-position:-100% 0} }

  /* ── ERROR ───────────────────────────────────────────────── */
  .game-error{
    max-width:780px; margin:2rem auto; padding:1rem 1.5rem;
    font-family:'Space Mono',monospace; font-size:0.78rem;
    color:var(--red); background:rgba(248,113,113,0.06);
    border:1px solid rgba(248,113,113,0.2); border-radius:var(--radius);
  }

  /* ── OVERLAY BACKDROP ────────────────────────────────────── */
  .overlay-dim{
    position:fixed; inset:0; z-index:499;
    background:rgba(0,0,0,0.7);
    opacity:0; transition:opacity 0.3s; pointer-events:none;
  }
  .overlay-dim.show{ opacity:1; pointer-events:auto; }
`;

// ── Hooks ────────────────────────────────────────────────────

function useTimer(running) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [running]);
  return seconds;
}

function fmt(s) {
  return `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
}

// ── Skeleton ─────────────────────────────────────────────────

function Skeleton() {
  const ws = [100,88,94,72,100,82,60,91,78,100,55,86,93,68,100];
  return (
    <div className="skeleton-wrap">
      {ws.map((w,i) => <div key={i} className="skel" style={{width:`${w}%`}} />)}
    </div>
  );
}

// ── WikiArticle renderer ─────────────────────────────────────

function WikiArticle({ html, onLinkClick }) {
  const ref = useCallback((node) => {
    if (!node) return;
    node.querySelectorAll("a").forEach((a) => {
      const href      = a.getAttribute("href") || "";
      const isAnomaly = a.classList.contains("anomaly-link");
      const match     = href.match(/^\/wiki\/([^:#]+)$/);
      if (match) {
        const title = decodeURIComponent(match[1]).replace(/_/g," ");
        a.href      = "#";
        a.onclick   = (e) => { e.preventDefault(); onLinkClick(title, isAnomaly); };
      } else {
        a.removeAttribute("href");
        a.style.cursor         = "default";
        a.style.color          = "inherit";
        a.style.textDecoration = "none";
      }
    });
    node.querySelectorAll(".mw-editsection,.noprint,.hatnote").forEach((el) => el.remove());
  }, [onLinkClick]);

  return (
    <div ref={ref} className="wiki-body" dangerouslySetInnerHTML={{ __html: html }} />
  );
}

// ── Main Component ───────────────────────────────────────────

/**
 * GameScreen manages the entire in-game session:
 *   - Loads Wikipedia pages via wikiAPI (with AI-generated anomalies)
 *   - Handles correct/wrong click logic and per-difficulty cooldown (5/10/15s)
 *   - Tracks hints (game-total for Detective, per-page for Scholar)
 *   - Fun Mode: unlimited hints + no cooldown on wrong clicks
 *
 * Props:
 *   difficulty     "scholar" | "detective" | "oracle"
 *   settings       { timer, clickCount, breadcrumb, funMode, ... }
 *   theme          Theme key for AI pool selection
 *   customPage     Optional starting article title
 *   customPages    Optional override for total page count
 *   mystery        Generated mystery object from App.js
 *   onWin(result)  Called when all pages are complete
 *   onQuit()       Called when player quits
 */
export default function GameScreen({ difficulty, settings, theme, customPage, customPages, onWin, onQuit }) {
  const totalPages   = customPages || TOTAL_PAGES[difficulty] || 4;
  const funMode      = settings?.funMode || false;
  const wrongCooldown = WRONG_COOLDOWNS[difficulty] ?? 5;  // per-difficulty penalty

  // Game-total hint limit (Infinity for Scholar/Fun Mode, 2 for Detective, 0 for Oracle)
  const gameHintLimit = funMode ? Infinity : (HINT_LIMITS[difficulty] ?? 0);
  const isUnlimited   = gameHintLimit === Infinity;

  // ── Page state ────────────────────────────────────────────
  const [pageNum,  setPageNum]  = useState(1);
  const [pageData, setPageData] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [error,    setError]    = useState(null);

  // ── Stats ─────────────────────────────────────────────────
  const [clicks,        setClicks]        = useState(0);
  const [correctHits,   setCorrectHits]   = useState(0);
  const [wrongHits,     setWrongHits]     = useState(0);
  const [hintsUsed,     setHintsUsed]     = useState(0);  // game-total hint counter
  const [pageHintsUsed, setPageHintsUsed] = useState(0);  // which hint to show next on this page

  // ── UI state ──────────────────────────────────────────────
  const [wrongPage,    setWrongPage]   = useState(false);
  const [hintMsg,      setHintMsg]     = useState(null);
  const [flash,        setFlash]       = useState(null);
  const [toast,        setToast]       = useState({ msg:"", type:"red", show:false });
  const [reveal,          setReveal]          = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // ── Wrong-click cooldown ──────────────────────────────────
  // Fun Mode bypasses the cooldown entirely (always 0)
  const [cooldown, setCooldown] = useState(0);

  // ── Breadcrumb ────────────────────────────────────────────
  const [breadcrumb, setBreadcrumb] = useState([]);

  // ── Timer ─────────────────────────────────────────────────
  const timerSeconds = useTimer(!loading && !!pageData && !isTransitioning);

  // ── Refs ──────────────────────────────────────────────────
  const flashTimer    = useRef(null);
  const toastTimer    = useRef(null);
  const revealTimer   = useRef(null);
  const articleWrapRef = useRef(null);  // for pixel-perfect overflow trimming

  // Build a page that always includes a valid anomaly.
  // Some Wikipedia pages can fail anomaly generation intermittently; we retry
  // before giving up so the player never lands on a "dead" page.
  const buildPageWithAnomaly = useCallback(async (title, forceTitle = false) => {
    const attempts = 3;
    for (let i = 0; i < attempts; i++) {
      const data = await buildGamePage(title, difficulty, theme, 8, forceTitle);
      if (data?.anomaly) return data;
    }
    throw new Error(`No anomaly generated for "${title || "random page"}" after ${attempts} attempts.`);
  }, [difficulty, theme]);

  // ── Pixel-perfect article trim ────────────────────────────
  // useLayoutEffect fires synchronously BEFORE the browser paints, so the
  // user never sees overflowing content — it's trimmed before first render.
  //
  // Strategy: remove the last paragraph repeatedly until the container's
  // scrollHeight equals its clientHeight (no overflow). We skip any
  // paragraph that contains the anomaly link so it's never lost.
  useLayoutEffect(() => {
    const wrap = articleWrapRef.current;
    if (!wrap || loading || !pageData) return;

    const body = wrap.querySelector(".wiki-body");
    if (!body) return;

    // Brief safety: run after the browser has computed layout for this render
    // (layout is ready inside useLayoutEffect, but we force a fresh measure)
    let guard = 0;
    while (wrap.scrollHeight > wrap.clientHeight + 2 && guard < 20) {
      const paras = Array.from(body.querySelectorAll("p"));
      if (paras.length <= 1) break; // always keep at least 1 paragraph

      // Walk backwards — skip any paragraph containing the anomaly
      let removed = false;
      for (let i = paras.length - 1; i >= 1; i--) {
        if (!paras[i].querySelector(".anomaly-link")) {
          paras[i].remove();
          removed = true;
          break;
        }
      }
      if (!removed) break; // only the anomaly paragraph left — stop
      guard++;
    }
  }, [pageData, loading]);

  // ── Hint availability ─────────────────────────────────────
  const pageHints = pageData?.anomaly?.hints || [];

  // Per-page hint behavior:
  // - Scholar: uses provided progressive hints (and free auto-hints on wrong clicks)
  // - Detective: allow up to 2 hints per page:
  //     1) normal hint (AI/Wikidata)
  //     2) reveal anomaly link text (costs a game hint)
  // - Oracle: no hints (unless Fun Mode)
  const maxHintsThisPage = funMode
    ? Infinity
    : difficulty === "detective"
      ? 2
      : pageHints.length;

  // canHint:
  //   Scholar   → per-page check only (unlimited total)
  //   Detective → per-page check AND game-total check (max 2 total)
  //   Oracle    → always false (unless Fun Mode overrides)
  //   Fun Mode  → always true when page hints are available
  const canHint = funMode
    ? !!pageData?.anomaly
    : (
        !!pageData?.anomaly &&
        pageHintsUsed < maxHintsThisPage &&
        (isUnlimited || hintsUsed < gameHintLimit) &&
        // If we have no AI/Wikidata hint text at all, we still allow hint #1
        // (generic) and hint #2 (reveal) for Detective.
        (difficulty === "detective" || pageHints.length > 0)
      );

  // ── Inject styles once ────────────────────────────────────
  useEffect(() => {
    const tag       = document.createElement("style");
    tag.textContent = STYLES;
    document.head.appendChild(tag);
    return () => tag.remove();
  }, []);

  // ── Cooldown countdown ────────────────────────────────────
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // ── Load page ─────────────────────────────────────────────
  // Always patches the breadcrumb with the RESOLVED Wikipedia title
  // (canonical title from parseData.parse.title — fixes redirect bug)
  const loadPage = useCallback(async (title, isInitial = false, forceTitle = false) => {
    setLoading(true);
    setRetrying(false);
    setError(null);
    setHintMsg(null);
    setWrongPage(false);
    setPageHintsUsed(0);  // reset per-page hint index on every new page

    try {
      setRetrying(true);
      const data = await buildPageWithAnomaly(title, forceTitle);
      setPageData(data);
      if (isInitial) {
        // For initial load: if the breadcrumb was pre-set with the requested title,
        // patch it now with the resolved canonical title
        setBreadcrumb((prev) =>
          prev.length === 0
            ? [{ title: data.title, status: "current" }]
            : prev.map((c, i) => i === prev.length - 1 ? { ...c, title: data.title } : c)
        );
      } else {
        // For navigation loads: patch the most recently added breadcrumb entry
        setBreadcrumb((prev) => prev.map((c, i) =>
          i === prev.length - 1 && c.status === "current"
            ? { ...c, title: data.title }
            : c
        ));
      }
    } catch (err) {
      // If a forceTitle load fails (bad anomaly searchTerm — not a real Wikipedia article),
      // silently fall back to a fresh theme page so the game continues uninterrupted.
      // Only show an error for initial/non-forced loads where there's no fallback.
      if (forceTitle) {
        console.warn(`[WikiScav] Could not load "${title}" — falling back to fresh theme page`);
        try {
          const fallback = await buildPageWithAnomaly(null, false);
          setPageData(fallback);
          setBreadcrumb((prev) => prev.map((c, i) =>
            i === prev.length - 1 && c.status === "current"
              ? { ...c, title: fallback.title }
              : c
          ));
          return; // recovered — don't set error
        } catch (fallbackErr) {
          console.warn("[WikiScav] Fallback also failed:", fallbackErr.message);
        }
      }
      setError(`Could not load "${title}". Check your connection.`);
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  }, [buildPageWithAnomaly]);

  // ── Initial load (StrictMode-safe) ───────────────────────
  // The `cancelled` flag prevents React.StrictMode's double-invoke
  // from running two concurrent page fetches in development.
  useEffect(() => {
    let cancelled = false;
    resetSession();

    (async () => {
      try {
        const startTitle = await fetchThemeTitle(theme, difficulty, customPage);
        if (cancelled) return;
        // Set initial breadcrumb with requested title — loadPage will patch to canonical
        setBreadcrumb([{ title: startTitle, status: "current" }]);
        await loadPage(startTitle, true);
      } catch {
        if (!cancelled) {
          setError("Could not connect to Wikipedia. Check your connection.");
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Feedback helpers ──────────────────────────────────────
  function triggerFeedback(type, message) {
    clearTimeout(flashTimer.current);
    clearTimeout(toastTimer.current);
    setFlash(type);
    setToast({ msg:message, type, show:true });
    flashTimer.current = setTimeout(() => setFlash(null), 650);
    toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, show:false })), 2500);
  }

  function showReveal(anomaly) {
    setReveal({ wrong:anomaly.wrongValue, real:anomaly.realValue, label:anomaly.factLabel, source:anomaly.source });
    clearTimeout(revealTimer.current);
    revealTimer.current = setTimeout(() => setReveal(null), 4000);
  }

  // ── Hint handler ──────────────────────────────────────────
  // Advances the per-page hint index and charges the game-total counter.
  // Scholar wrong-click auto-hints use a separate path (no game-total charge).
  function handleHint() {
    if (!canHint || !pageData?.anomaly) return;
    const hints   = pageData.anomaly.hints || [];
    const nextIdx = pageHintsUsed;

    // Fun Mode hint behavior:
    // - Hint 1: normal (uses provided hint if any)
    // - Hint 2+: explicit reveal of the anomaly text
    const msg = funMode
      ? (nextIdx === 0
          ? (hints[0] || "💡 Hint: Read carefully — the anomaly is a single wrong hyperlink.")
          : `REVEAL: The anomaly link text is “${pageData.anomaly.wrongValue}”.`
        )
      : (difficulty === "detective" && nextIdx >= 1
          ? `REVEAL: The anomaly link text is “${pageData.anomaly.wrongValue}”.`
          : (hints[0] || "The replaced entity is related to a key fact in this article.")
        );

    setPageHintsUsed((n) => n + 1);
    // Track hint usage, but scoring ignores it in Fun Mode (handled at WinScreen).
    setHintsUsed((h) => h + 1);

    setHintMsg(msg);

    const gameRemaining = (funMode || isUnlimited) ? "∞" : Math.max(0, gameHintLimit - (hintsUsed + 1));
    const label = (funMode || isUnlimited)
      ? `💡 Hint ${nextIdx + 1} used`
      : gameRemaining > 0
        ? `💡 Hint used — ${gameRemaining} game hint${gameRemaining !== 1 ? "s" : ""} left`
        : `💡 Last hint used!`;

    triggerFeedback("gold", label);
  }

  // ── Main link click handler ───────────────────────────────
  async function handleLinkClick(title, isAnomaly) {
    if (isTransitioning) return;
    if (cooldown > 0 && !funMode) return;  // Fun Mode ignores cooldown lock

    const newClicks = clicks + 1;
    setClicks(newClicks);

    if (isAnomaly) {
      // ── CORRECT CLICK ─────────────────────────────────────
      const newCorrectHits = correctHits + 1;
      setCorrectHits(newCorrectHits);
      setWrongPage(false);
      if (pageData?.anomaly) showReveal(pageData.anomaly);

      const isGameOver = pageNum >= totalPages;
      const finalBreadcrumb = [
        ...breadcrumb.map((c) => c.status === "current" ? { ...c, status:"correct" } : c),
      ];

      if (isGameOver) {
        onWin({
          clicks:      newClicks,
          seconds:     timerSeconds,
          correctHits: newCorrectHits,
          wrongHits,
          hintsUsed,
          totalPages,
          pagesVisited: finalBreadcrumb,
          difficulty,
          funMode,
        });
        return;
      }

      triggerFeedback("green", "Anomaly found! Loading next page…");
      const nextTitle = pageData?.anomaly?.searchTerm || title;
      setPageNum((n) => n + 1);
      setBreadcrumb((prev) => [
        ...prev.map((c) => c.status === "current" ? { ...c, status:"correct" } : c),
        { title: nextTitle, status:"current" },
      ]);
      setIsTransitioning(true);
      await loadPage(nextTitle, false, true);
      setIsTransitioning(false);

    } else {
      // ── WRONG CLICK ──────────────────────────────────────
      setWrongHits((w) => w + 1);
      setWrongPage(true);

      if (!funMode) {
        setCooldown(wrongCooldown);
      }

      // Scholar gets a free progressive auto-hint on wrong click (no game-total charge)
      if (difficulty === "scholar" && pageData?.anomaly) {
        const hints   = pageData.anomaly.hints || [];
        const nextIdx = pageHintsUsed;
        if (nextIdx < hints.length) {
          setHintMsg(hints[nextIdx]);
          setPageHintsUsed(nextIdx + 1);
          triggerFeedback("red",
            funMode
              ? `Wrong link! Fun Mode — no cooldown`
              : `Wrong link — ${wrongCooldown}s penalty! (Free hint revealed)`
          );
        } else {
          triggerFeedback("red",
            funMode ? `Wrong link! Fun Mode — no cooldown` : `Wrong link — ${wrongCooldown}s penalty!`
          );
        }
      } else {
        triggerFeedback("red",
          funMode ? `Wrong link! Fun Mode — no cooldown` : `Wrong link — ${wrongCooldown}s penalty!`
        );
      }
    }
  }

  // ── Hint bar display string ───────────────────────────────
  const hintsLeftDisplay = (() => {
    if (funMode) return "🎉 Fun Mode — Unlimited hints";
    if (isUnlimited) return "Unlimited hints available";
    const left = Math.max(0, gameHintLimit - hintsUsed);
    if (left === 0) return "No hints remaining";
    return `${left} game hint${left !== 1 ? "s" : ""} remaining`;
  })();

  // ── Render ────────────────────────────────────────────────
  return (
    <div style={{ minHeight:"100vh", background:"var(--bg)", color:"var(--text)" }}>

      {/* Anomaly reveal strip */}
      {reveal && (
        <div className="reveal-panel show">
          <span className="rp-title">✓ Anomaly Identified</span>
          <div>
            <span className="rp-wrong">{reveal.wrong}</span>
            <span className="rp-arrow">→</span>
            <span className="rp-real">{reveal.real}</span>
          </div>
          <span className="rp-label">The {reveal.label} was changed</span>
          {reveal.source === "ai" && (
            <span className="rp-source">✦ AI-generated anomaly</span>
          )}
        </div>
      )}

      {/* Flash overlay */}
      <div className={[
        "flash-overlay",
        flash === "red"   ? "flash-red"   : "",
        flash === "green" ? "flash-green" : "",
      ].join(" ")} />

      {/* Toast */}
      <div className={`toast toast-${toast.type} ${toast.show ? "show" : ""}`}>
        {toast.msg}
      </div>

      {/* ── Top bar ──────────────────────────────────────────── */}
      <div className="game-topbar">
        <div className="topbar-brand">
          <span className="topbar-logo">🌐</span>
          <span className="topbar-title">WikiScav</span>
          <div className="topbar-meta">
            {theme && THEMES[theme] && (
              <span className="topbar-chip chip-theme">
                {THEMES[theme].emoji} {THEMES[theme].label}
              </span>
            )}
            <span className={`topbar-chip chip-${difficulty}`}>
              {difficulty === "scholar" ? "Scholar · Easy"
                : difficulty === "detective" ? "Detective · Med"
                : "Oracle · Hard"}
            </span>
          </div>
        </div>

        <div className="topbar-progress">
          <span className="prog-label">Progress</span>
          <div className="prog-dots">
            {Array.from({ length: totalPages }).map((_, i) => {
              const status = i < correctHits ? "done" : i === pageNum - 1 ? "active" : "todo";
              return <div key={i} className={`prog-dot ${status}`} />;
            })}
          </div>
          <span className="prog-text">Page {pageNum} of {totalPages}</span>
        </div>

        <div className="topbar-stats">
          {settings?.timer !== false && (
            <div className="tstat">
              <span className="ts-label">Time</span>
              <span className="ts-value">{fmt(timerSeconds)}</span>
            </div>
          )}
          {settings?.clickCount !== false && (
            <div className="tstat">
              <span className="ts-label">Clicks</span>
              <span className="ts-value">{clicks}</span>
            </div>
          )}
          {wrongHits > 0 && (
            <div className="tstat">
              <span className="ts-label">Wrong</span>
              <span className="ts-value" style={{color:"var(--red)"}}>{wrongHits}</span>
            </div>
          )}
          {funMode && <span className="fun-mode-badge">🎉 Fun Mode</span>}
          <button
            className="btn-quit"
            onClick={() => {
              // "Give up" ends the run and reveals the correct answer on a loss screen.
              // We surface it via onWin-like result channel using a custom event.
              // App.js will route it to LoseScreen.
              const giveUpEvent = new CustomEvent("wikiscav:giveup", {
                detail: {
                  seconds: timerSeconds,
                  wrongHits,
                  hintsUsed,
                  totalPages,
                  correctHits,
                  difficulty,
                  funMode,
                  pageNum,
                  pageTitle: pageData?.title || "",
                  anomaly: pageData?.anomaly || null,
                  pagesVisited: breadcrumb,
                },
              });
              window.dispatchEvent(giveUpEvent);
            }}
            style={{ borderColor:"rgba(248,113,113,0.35)", color:"rgba(248,113,113,0.85)" }}
          >
            GIVE UP
          </button>
          <button className="btn-quit" onClick={onQuit}>QUIT</button>
        </div>
      </div>

      {/* Retry banner */}
      {retrying && (
        <div className="retry-banner">
          ⏳ Generating anomaly — one moment…
        </div>
      )}

      {/* Wrong-click banner */}
      {wrongPage && !loading && (
        <div className="wrong-banner">
          <div className="wrong-banner-left">
            <span className="wrong-banner-title">✗ Not the Anomaly</span>
            <span className="wrong-banner-msg">
              That link is real. The false fact is still hiding somewhere on this page.
              {cooldown > 0 && !funMode && (
                <> &nbsp;—&nbsp;
                  <span style={{ color:"var(--red)", fontWeight:700 }}>
                    {cooldown}s before you can click again
                  </span>
                </>
              )}
              {funMode && " Fun Mode — no cooldown!"}
            </span>
          </div>
          {(cooldown <= 0 || funMode) && (
            <button className="btn-dismiss" onClick={() => setWrongPage(false)}>
              Dismiss
            </button>
          )}
        </div>
      )}

      {/* Hint bar — hidden for Oracle without Fun Mode */}
      {!loading && pageData && (difficulty !== "oracle" || funMode) && (
        <div className="hint-bar">
          <div>
            {hintMsg
              ? <span className="hint-msg">💡 {hintMsg}</span>
              : <span className="hint-count">{hintsLeftDisplay}</span>
            }
          </div>
          <button
            className="btn-hint"
            onClick={handleHint}
            disabled={!canHint || !pageData.anomaly}
          >
            {funMode
              ? (pageHintsUsed >= 1 ? "👁 Reveal Anomaly" : "💡 Use Hint")
              : (difficulty === "detective"
                  ? (pageHintsUsed >= 1 ? "👁 Reveal Anomaly" : "💡 Use Hint")
                  : (canHint ? "💡 Use Hint" : pageHintsUsed >= pageHints.length ? "No more hints for this page" : "No hints left")
                )
            }
          </button>
        </div>
      )}

      {/* Breadcrumb */}
      {settings?.breadcrumb !== false && breadcrumb.length > 0 && (
        <div className="breadcrumb-bar">
          {breadcrumb.map((c, i) => (
            <span key={i}>
              <span className={`bc-crumb ${c.status}`}>{c.title}</span>
              {i < breadcrumb.length - 1 && <span className="bc-sep"> → </span>}
            </span>
          ))}
        </div>
      )}

      {/* Article */}
      {error && <div className="game-error">⚠ {error}</div>}

      {loading
        ? <Skeleton />
        : pageData && (
          <div
            ref={articleWrapRef}
            className={`article-wrap${cooldown > 0 && !funMode ? " on-cooldown" : ""}`}
          >
            <h1 className="article-title">{pageData.title}</h1>
            <WikiArticle html={pageData.html} onLinkClick={handleLinkClick} />
          </div>
        )
      }
    </div>
  );
}
